/**
 * Vocabulary shared with the game (DESIGN.md §7, §14, §15). The game's
 * data/*.json is the source of truth; these unions exist so the codex UI can
 * colour and order things without re-reading the JSON.
 */
export const ELEMENTS = ["fire", "water", "electric", "wind", "ground", "poison", "ice"] as const;
export type Element = (typeof ELEMENTS)[number];

export const GEAR_RARITIES = ["common", "uncommon", "rare", "epic", "legendary", "mythic"] as const;
export type GearRarity = (typeof GEAR_RARITIES)[number];

/** Skills have no Uncommon tier (§13 DECIDED, carried into §15). */
export const SKILL_RARITIES = ["common", "rare", "epic", "legendary", "mythic"] as const;
export type SkillRarity = (typeof SKILL_RARITIES)[number];

export const GEAR_SLOTS = ["weapon", "earrings", "armor", "ring", "helmet", "boots"] as const;
export type GearSlotId = (typeof GEAR_SLOTS)[number];

export const STAGE_EVENTS = ["battle", "skill_selection", "boon_selection", "mini_boss", "boss"] as const;
export type StageEvent = (typeof STAGE_EVENTS)[number];

export const HOOKS = ["", "on_hit_dealt", "on_hit_incoming", "on_hit_taken", "on_kill", "every_n_attacks", "every_n_seconds"] as const;
export type Hook = (typeof HOOKS)[number];

/** Human labels for the wire vocabulary. One place, so a rename lands everywhere. */
export const ELEMENT_LABEL: Record<Element, string> = {
	fire: "Fire", water: "Water", electric: "Electric", wind: "Wind", ground: "Ground", poison: "Poison", ice: "Ice",
};
export const ELEMENT_IDENTITY: Record<Element, string> = {
	fire: "damage over time",
	water: "sustain: heal and buff on attack",
	electric: "crowd control and ultimate charge",
	wind: "crit, parry, extra hits",
	ground: "anti-ultimate, shields, reflect",
	poison: "stacking damage over time",
	ice: "hard crowd control",
};
export const RARITY_LABEL: Record<GearRarity, string> = {
	common: "Common", uncommon: "Uncommon", rare: "Rare", epic: "Epic", legendary: "Legendary", mythic: "Mythic",
};
export const SLOT_LABEL: Record<GearSlotId, string> = {
	weapon: "Weapon", earrings: "Earrings", armor: "Armor", ring: "Ring", helmet: "Helmet", boots: "Boots",
};
export const STAGE_EVENT_LABEL: Record<StageEvent, string> = {
	battle: "Battle", skill_selection: "Skill Selection", boon_selection: "Boon Selection", mini_boss: "Mini-boss", boss: "Boss",
};
export const HOOK_LABEL: Record<Hook, string> = {
	"": "Passive",
	on_hit_dealt: "When you attack",
	on_hit_incoming: "Before you are hit",
	on_hit_taken: "When you are hit",
	on_kill: "On kill",
	every_n_attacks: "Every Nth attack",
	every_n_seconds: "On a cooldown",
};

/**
 * Effect types the game already handles (perks.json / skills.json) plus a few
 * the designers keep asking for. Only suggestions: the catalog cell is free text.
 */
export const EFFECT_TYPE_SUGGESTIONS = [
	"flat_stat_percent", "damage_reduction", "shield_on_hit", "thorns", "lifesteal", "crit", "crit_damage",
	"double_hit", "dodge", "coin_bonus", "xp_bonus", "scroll_drop_bonus", "element_damage", "element_resist",
] as const;
